import { Controller } from '@hotwired/stimulus';
import { trackEvent } from '../analytics';
import { arrivedFromInstall, clearInstallMarker, markExtensionInstallPending } from './helpers/extension_install_signal';
import { extensionPresent } from './helpers/extension_presence';
import { clearWentToStore, storeParams, wentToStore } from './helpers/extension_store';

// Fires the extension_installed conversion (all stores — see analytics.js), tagged with store and, for
// Chromium, browser_brand params so a single event breaks down per browser. The Web Store strips the
// gclid, so ad traffic must land here first for GA4 to attribute it. Only an absent -> present transition
// across two pageviews counts; presence alone would convert everyone who already had it. Cookies, not
// localStorage: lite and the apex are separate origins. The store click is counted separately, by
// extension_store_link_controller.
//
// The install redirect's `installed=1` marker is the one exception to that transition rule — it stands in
// for a prior absence verdict. So the extension must send it only for onInstalled reason 'install': an
// 'update' redirect would re-count every existing user whose counted cookie has since lapsed.
//
// It also reports presenceEvent on every detection, not just new ones — see #reportPresence.

const SAW_NO_EXTENSION_COOKIE = 'unscreen_saw_no_extension';
const INSTALL_COUNTED_COOKIE = 'unscreen_ext_install_counted';

// Only has to outlive a trip to the Web Store, but people put it off. Capped at the Ads click-through
// window, past which a conversion can't be attributed to the click that paid for it anyway.
const SAW_NO_EXTENSION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

// Chrome's maximum, on purpose: this is what stops a stale absence flag re-counting an install.
const INSTALL_COUNTED_MAX_AGE_SECONDS = 400 * 24 * 60 * 60;

export default class extends Controller {
  static targets = ['installAlert'];
  static values = { installEvent: String, presenceEvent: String, cookieDomain: String, detectedSurface: String, detectedSignedIn: Boolean };

  connect() {
    this.detected = false;
    this.onVisibilityChange = () => this.#reloadIfBackFromStore();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.#watchForExtension();
  }

  disconnect() {
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  // Safe to trust the DOM as rendered: Turbo Drive is off (application.js), so the extension's
  // content script gets a fresh run at every navigation.
  #watchForExtension() {
    // Snapshot first: the conversion must turn on a previous pageview's verdict, not this load's own. The
    // install redirect's marker stands in for that verdict on pages that never rendered the alert.
    this.installIsNew = this.#readCookie(SAW_NO_EXTENSION_COOKIE) === '1' || arrivedFromInstall();
    // The extension opens its post-install landing in a fresh tab, so no reload ever wrote the marker — yet
    // this is the same just-installed moment, reached by the other door. Must run before the strip below.
    // Nothing reads the marker today; it is what the first-launch glow will arm on (UNS-5263).
    if (arrivedFromInstall()) markExtensionInstallPending();
    clearInstallMarker();

    // All three are proof: only the extension removes the alert.
    if (extensionPresent()) return this.#extensionDetected('attribute');
    if (!this.hasInstallAlertTarget) return this.#extensionDetected('alert_gone_on_connect');

    // Not legacy: the first load after an install stamps too late, so this is its only signal (UNS-4945).
    this.observer = new MutationObserver(() => {
      if (!this.hasInstallAlertTarget) this.#extensionDetected('alert_removed');
    });
    this.observer.observe(this.element, { childList: true, subtree: true });
    this.#writeCookie(SAW_NO_EXTENSION_COOKIE, '1', SAW_NO_EXTENSION_MAX_AGE_SECONDS);
  }

  async #extensionDetected(detectedVia) {
    if (this.detected) return;
    this.detected = true;
    this.observer?.disconnect();

    this.#reportPresence(detectedVia);

    // Drop this load's own absence flag, or the next pageview counts a late extension as an install.
    if (!this.installIsNew || this.#readCookie(INSTALL_COUNTED_COOKIE) === '1') {
      this.#deleteCookie(SAW_NO_EXTENSION_COOKIE);
      return;
    }

    // detected_*, not surface/signed_in: detection lands on a later pageview than the click, often on the
    // other origin, so it says where we noticed the install — not where they clicked.
    const params = {
      ...storeParams(),
      detected_surface: this.detectedSurfaceValue,
      detected_signed_in: this.detectedSignedInValue,
    };

    // Claimed before the send, not after: the redirect's landing tab and the original tab's post-store
    // reload both detect at about the same moment, and the await below is long enough (GTAG_LOAD_TIMEOUT_MS)
    // for both to get past the check above and count the same install twice.
    this.#writeCookie(INSTALL_COUNTED_COOKIE, '1', INSTALL_COUNTED_MAX_AGE_SECONDS);

    // On a miss put both flags back as they were and let a later pageview retry — including the absence
    // flag, which a tab that lost the claim above will have dropped on its way out.
    if (!(await trackEvent(this.installEventValue, params))) {
      this.#deleteCookie(INSTALL_COUNTED_COOKIE);
      this.#writeCookie(SAW_NO_EXTENSION_COOKIE, '1', SAW_NO_EXTENSION_MAX_AGE_SECONDS);
      return;
    }

    this.#deleteCookie(SAW_NO_EXTENSION_COOKIE);
  }

  // posthog, not trackEvent: telemetry, not a conversion, so it stays out of GA4 and Ads.
  #reportPresence(detectedVia) {
    if (!this.presenceEventValue) return;

    window.posthog?.capture(this.presenceEventValue, { surface: this.detectedSurfaceValue, detected_via: detectedVia });
  }

  // Chrome won't inject content scripts into an already-open tab, so it stays blind until reloaded.
  #reloadIfBackFromStore() {
    if (document.visibilityState !== 'visible') return;
    if (this.detected) return;
    if (!wentToStore()) return;

    // Cleared first so the fresh load can't bounce again.
    clearWentToStore();
    markExtensionInstallPending();
    window.location.reload();
  }

  #readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? match[1] : null;
  }

  // Client-side so lite's CDN-cached response never carries a Set-Cookie header.
  #writeCookie(name, value, maxAge) {
    document.cookie = `${name}=${value}; ${this.#cookieOptions(maxAge)}`;
  }

  #deleteCookie(name) {
    document.cookie = `${name}=; ${this.#cookieOptions(0)}`;
  }

  // Blank domain falls back to a host-only cookie.
  #cookieOptions(maxAge) {
    const domain = this.cookieDomainValue ? `domain=${this.cookieDomainValue}; ` : '';
    return `path=/; ${domain}max-age=${maxAge}; SameSite=Lax; Secure`;
  }
}
